import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'src/components/atoms';
import {
  EditableTitleInput,
  EditableTitleStyled,
  EditableTitleValue,
} from './EditableTitle.styles';

interface Props {
  value: string;
  setValue: (value: string) => void;
  defaultValue?: string;
  tooltipText?: string;
  disabled?: boolean;
  onSave?: () => void;
  'data-testid'?: string;
  fixedHeight?: boolean;
  maxLength?: number;
}

export const EditableTitle = ({
  value,
  setValue,
  defaultValue,
  tooltipText = 'Rename',
  disabled,
  onSave,
  fixedHeight,
  maxLength,
  ...props
}: Props) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const titleValue = (value || defaultValue) ?? '';

  const handleClick: React.MouseEventHandler = (event) => {
    if (!disabled) {
      event.stopPropagation();
      if (!isEditing) {
        setTooltipVisible(false);
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus?.(), 0);
      }
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const next = event.target.value;
    setValue(
      maxLength != null && next.length > maxLength
        ? next.slice(0, maxLength)
        : next,
    );
  };

  const handleBlur: React.FocusEventHandler = () => {
    setIsEditing(false);
    onSave?.();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  return (
    <>
      <EditableTitleStyled
        onClick={handleClick}
        {...props}
        isEditing={isEditing}
      >
        {isEditing ? (
          <EditableTitleInput
            value={value}
            isEditing={isEditing}
            onChange={handleChange}
            onBlur={handleBlur}
            ref={inputRef}
            type="text"
            onKeyDown={handleKeyDown}
            maxLength={maxLength}
          />
        ) : (
          <EditableTitleValue
            ref={setRoot}
            onMouseOver={() => {
              if (!disabled) {
                setTooltipVisible(true);
              }
            }}
            onMouseOut={() => {
              if (!disabled) {
                setTooltipVisible(false);
              }
            }}
            fixedHeight={fixedHeight}
          >
            {titleValue}
          </EditableTitleValue>
        )}
      </EditableTitleStyled>
      {Boolean(tooltipText) &&
        tooltipVisible &&
        root &&
        createPortal(
          <Tooltip text={tooltipText} variant="up" root={root} />,
          document.body,
        )}
    </>
  );
};
